import { CrossQueryCacheSharingConfig, InfiniteQueryMethodForModel, ModelName, QueryMethodForModel } from "../types";

/**
 * Arguments for {@link shouldCrossShareQueryCache}.
 *
 * `config` mirrors the `crossQueryCacheSharing` option accepted by query
 * hooks — `true`/`false` applies globally, while a `CrossQueryCacheSharingConfig`
 * object enables sharing per model or per model+method pair.
 */
interface ShouldCrossShareQueryCacheArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> | InfiniteQueryMethodForModel<Model>,
> {
  config?: boolean | CrossQueryCacheSharingConfig;
  model: Model;
  method: Method;
}

/**
 * Resolves whether cross-query cache sharing should be active for a specific
 * model and method combination, given the consumer's config value.
 *
 * Resolution order:
 * 1. If `config` is a boolean (or `undefined`), return it directly.
 * 2. If `config.models` is a `ModelName[]`, return whether `model` is in the array.
 * 3. Otherwise treat `config.models` as a per-model object and check whether
 *    `method` is listed under `config.models[model].methods`.
 *
 * @param config - The `crossQueryCacheSharing` value from a query hook's options.
 * @param model - The model the query is operating on.
 * @param method - The query method being called (e.g. `"getOne"`, `"getSome"`).
 * @returns `true` if cross-query cache sharing is enabled for this combination.
 */
export const shouldCrossShareQueryCache = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> | InfiniteQueryMethodForModel<Model>,
>({
  config,
  model,
  method,
}: ShouldCrossShareQueryCacheArgs<Model, Method>): boolean => {
  if (typeof config === "boolean" || config === undefined) {
    return !!config;
  }

  if (Array.isArray(config.models)) {
    return config.models.includes(model);
  }

  return !!config.models?.[model]?.methods?.includes(method);
};
